import { useState, useEffect, useCallback } from "react";
import {
  AlertTriangle, Bug, Monitor, Server, Trash2, Search,
  RefreshCw, ChevronLeft, ChevronRight, Filter, Clock, Loader2
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import api from "../../lib/api";
import { cn } from "../../lib/utils";

const SEVERITY_CONFIG = {
  critical: { color: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300", icon: AlertTriangle },
  error: { color: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300", icon: Bug },
  warning: { color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300", icon: AlertTriangle },
  info: { color: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300", icon: Monitor },
};

const ErrorLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [severity, setSeverity] = useState("all");
  const [source, setSource] = useState("all");
  const [search, setSearch] = useState("");
  const [severityCounts, setSeverityCounts] = useState({});
  const [selectedLog, setSelectedLog] = useState(null);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 30 });
      if (severity !== "all") params.append("severity", severity);
      if (source !== "all") params.append("source", source);
      if (search) params.append("search", search);

      const res = await api.get(`/error-logs?${params}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setSeverityCounts(res.data.severity_counts || {});
    } catch {
      toast.error("Failed to fetch error logs");
    } finally {
      setLoading(false);
    }
  }, [page, severity, source, search]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleClear = async () => {
    try {
      const params = severity !== "all" ? `?severity=${severity}` : "";
      await api.delete(`/error-logs${params}`);
      toast.success("Error logs cleared");
      setClearDialogOpen(false);
      fetchLogs();
    } catch {
      toast.error("Failed to clear logs");
    }
  };

  const totalErrors = Object.values(severityCounts).reduce((a, b) => a + b, 0);

  const formatTime = (ts) => {
    if (!ts) return "—";
    const d = new Date(ts);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  return (
    <AdminLayout title="Error Logs">
      <div className="space-y-4" data-testid="error-logs-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight" data-testid="error-logs-title">System Error Logs</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {totalErrors} total error{totalErrors !== 1 ? "s" : ""} logged
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchLogs} data-testid="refresh-logs-btn">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setClearDialogOpen(true)} data-testid="clear-logs-btn" disabled={totalErrors === 0}>
              <Trash2 className="w-4 h-4 mr-1.5" /> Clear
            </Button>
          </div>
        </div>

        {/* Severity Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {["critical", "error", "warning", "info"].map((sev) => {
            const cfg = SEVERITY_CONFIG[sev];
            const Icon = cfg.icon;
            return (
              <Card
                key={sev}
                className={cn("cursor-pointer transition-all hover:ring-1 hover:ring-primary/30", severity === sev && "ring-2 ring-primary")}
                onClick={() => setSeverity(severity === sev ? "all" : sev)}
                data-testid={`severity-card-${sev}`}
              >
                <CardContent className="py-3 px-4 flex items-center gap-3">
                  <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0", cfg.color)}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-lg font-bold leading-none">{severityCounts[sev] || 0}</p>
                    <p className="text-[11px] text-muted-foreground capitalize mt-0.5">{sev}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search errors..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-9 h-9"
              data-testid="error-search"
            />
          </div>
          <Select value={source} onValueChange={(v) => { setSource(v); setPage(1); }}>
            <SelectTrigger className="w-[140px] h-9" data-testid="source-filter">
              <Filter className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="backend">Backend</SelectItem>
              <SelectItem value="frontend">Frontend</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Logs Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16" data-testid="error-logs-loading">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16" data-testid="no-errors">
            <Bug className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">No errors logged. System is running cleanly.</p>
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="error-logs-list">
            {logs.map((log, i) => {
              const cfg = SEVERITY_CONFIG[log.severity] || SEVERITY_CONFIG.error;
              const Icon = log.source === "frontend" ? Monitor : Server;
              return (
                <button
                  key={i}
                  onClick={() => setSelectedLog(log)}
                  className="w-full text-left p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors flex items-start gap-3"
                  data-testid={`error-log-${i}`}
                >
                  <Icon className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={cn("text-[10px] px-1.5", cfg.color)}>{log.severity}</Badge>
                      <span className="text-xs text-muted-foreground font-mono">{log.endpoint}</span>
                    </div>
                    <p className="text-sm mt-1 truncate">{log.message}</p>
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatTime(log.timestamp)}</span>
                      {log.user_email && <span>{log.user_email}</span>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-muted-foreground">
              Page {page} of {pages} ({total} total)
            </p>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="prev-page">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="next-page">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Detail Dialog */}
        <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bug className="w-5 h-5" /> Error Detail
              </DialogTitle>
            </DialogHeader>
            {selectedLog && (
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <Badge className={cn("text-xs", SEVERITY_CONFIG[selectedLog.severity]?.color)}>{selectedLog.severity}</Badge>
                  <Badge variant="outline" className="text-xs">{selectedLog.source}</Badge>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Message</p>
                  <p>{selectedLog.message}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Endpoint</p>
                  <code className="text-xs bg-muted px-2 py-1 rounded block">{selectedLog.endpoint}</code>
                </div>
                {selectedLog.stack_trace && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Stack Trace</p>
                    <pre className="text-[11px] bg-muted p-3 rounded-lg overflow-x-auto whitespace-pre-wrap break-all max-h-48 overflow-y-auto">{selectedLog.stack_trace}</pre>
                  </div>
                )}
                {selectedLog.component && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Component</p>
                    <code className="text-xs bg-muted px-2 py-1 rounded">{selectedLog.component}</code>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <p className="font-medium text-muted-foreground mb-0.5">User</p>
                    <p>{selectedLog.user_email || "—"}</p>
                  </div>
                  <div>
                    <p className="font-medium text-muted-foreground mb-0.5">Time</p>
                    <p>{formatTime(selectedLog.timestamp)}</p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Clear Dialog */}
        <Dialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Clear Error Logs</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              {severity !== "all"
                ? `This will delete all "${severity}" level logs. This action cannot be undone.`
                : "This will delete ALL error logs. This action cannot be undone."
              }
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setClearDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleClear} data-testid="confirm-clear-btn">Delete Logs</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default ErrorLogsPage;
